import { useEffect, useState } from 'react';

import classNames from 'classnames';
import BaseStyling from 'config/baseStyling';
import styled from 'styled-components';

import type { DialogProps } from '@mui/material/Dialog';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

import { Icon } from '@sdb/planning-ui';

const StyledDialog = styled(Dialog)`
  height: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    font-size: 18px;
  }

  .MuiDialogContent-root {
    flex-grow: 1;
  }

  .MuiDialogActions-root {
    padding: 20px 0 0;
  }
`;

// `title` is omitted: the DOM `title` attribute is a string, and intersecting it with ReactNode
// collapsed the union below to string-only.
type Props = Omit<DialogProps, 'title'> & {
  hideTitle?: boolean;
  open: boolean;
  title?: string | React.ReactNode;
  headerBgColor?: string;
  hideCloseButton?: true;
  disableBackdropClick?: true;
  onHidden?: () => void;
  handleClose?: () => void;
  /**
   * Rendered as a sibling of the content instead of inside it, so it stays
   * outside the scroll area and keeps hugging the bottom of the dialog.
   */
  actions?: React.ReactNode;
};

export default function CustomDialog(props: Props) {
  const {
    hideTitle = false,
    open = true,
    onHidden,
    children,
    title = '',
    headerBgColor = '',
    hideCloseButton,
    handleClose,
    disableBackdropClick,
    actions,
    onClose: onCloseProp,
    ...rest
  } = props;
  // A caller that supplies `onClose` owns closing, even when it supplies it as
  // `undefined` (an optional prop left unset by its own caller) — that used to
  // reach MUI as `undefined` and make click-away/Escape inert, so keep it inert
  // rather than falling back to the built-in self-close.
  const callerOwnsClose = 'onClose' in props;
  const [isClosed, setClosed] = useState(false);

  const onClose = () => {
    if (handleClose) {
      handleClose();
    } else {
      setClosed(true);
    }
  };

  useEffect(() => {
    if (!open) {
      setClosed(false);
    }
  }, [open]);

  return (
    <StyledDialog
      // `disableBackdropClick` drops only the click-away; Escape still closes,
      // as it does in every dialog (and as keyboard users expect). For these
      // MUI-initiated closes a caller's own `onClose` replaces the built-in
      // close (as it always did) but is pulled out of `rest` so it cannot
      // bypass the guard. The title-bar close icon is a separate path: it goes
      // through `handleClose` / the built-in close, not through `onClose`.
      onClose={(event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') return;
        if (callerOwnsClose) {
          onCloseProp?.(event, reason);
        } else {
          onClose();
        }
      }}
      TransitionProps={{ onExited: onHidden }}
      open={isClosed ? false : open}
      {...rest}
      // A dialog opened on top of a dijit dialog (e.g. the employee dialog in
      // the roster) is invisible to dijit's focus manager: it sees focus land
      // outside its own dialog and yanks it straight back, so every keystroke
      // is lost. dijit skips any node with a `dijitPopup` ancestor, which is
      // exactly what this dialog is from its point of view. Carries no dijit
      // styling of its own — MUI's own rules win.
      className={classNames('dijitPopup', rest.className)}
    >
      {!hideTitle && (
        <DialogTitle
          className={classNames('d-flex', 'center', title ? 'between' : 'end')}
          style={headerBgColor ? { backgroundColor: headerBgColor } : undefined}
        >
          {title && (
            <span style={headerBgColor ? { color: 'white' } : undefined}>
              {title}
            </span>
          )}
          {!hideCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              style={{ marginLeft: '10px' }}
              size="large"
            >
              <Icon
                type="xmark"
                style={headerBgColor ? { color: 'white' } : undefined}
              />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent dividers>
        <BaseStyling>{children}</BaseStyling>
      </DialogContent>

      {actions}
    </StyledDialog>
  );
}
